import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

const DISMISSED_KEY = 'aga.installPromptDismissedAt';
const DISMISS_DAYS = 7;

function isAlreadyInstalled() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS standalone flag
  if (window.navigator.standalone) return true;
  return false;
}

function isRecentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISSED_KEY);
    if (!v) return false;
    const dismissedAt = parseInt(v, 10);
    if (Number.isNaN(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    return false;
  }
}

function isIos() {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

/**
 * Floating "Install App" button (PWA Add-to-Home-Screen prompt).
 * - Listens for `beforeinstallprompt` and shows the FAB when installable
 * - On iOS (which has no programmatic prompt), shows manual instructions
 * - Hides itself for 7 days when user clicks dismiss
 */
export function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosInfoOpen, setIosInfoOpen] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled() || isRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari has no event — show after a small delay if it's iOS
    let iosTimer = null;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (isIos() && !deferredPrompt) {
      setIosInfoOpen(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (err) { /* localStorage unavailable */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-1 group"
        data-testid="install-app-fab"
      >
        <button
          onClick={install}
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#007AFF] text-[#000B18] font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
          data-testid="install-app-btn"
          aria-label="Install AGA app"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Install App</span>
          <span className="sm:hidden text-sm">Install</span>
        </button>
        <button
          onClick={dismiss}
          className="p-2 rounded-full bg-[#041A3B] border border-white/10 text-[#A0B4CC] hover:text-white opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
          data-testid="install-app-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {iosInfoOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={() => setIosInfoOpen(false)}
          data-testid="ios-install-modal"
        >
          <div
            className="bg-[#041A3B] border border-white/10 rounded-xl p-6 max-w-sm w-full text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-outfit text-lg font-semibold mb-2">Install on iPhone / iPad</h3>
            <ol className="text-sm text-[#A0B4CC] space-y-2 mb-4 list-decimal pl-5">
              <li>Tap the <span className="text-white font-medium">Share</span> button in Safari</li>
              <li>Scroll and choose <span className="text-white font-medium">Add to Home Screen</span></li>
              <li>Tap <span className="text-white font-medium">Add</span> in the top-right corner</li>
            </ol>
            <Button
              onClick={() => setIosInfoOpen(false)}
              className="w-full btn-primary"
              data-testid="ios-install-close-btn"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
